import { html, css, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { dom, library } from '@fortawesome/fontawesome-svg-core';
import { faExchange, faEdit, faTags } from '@fortawesome/pro-solid-svg-icons';

import { svgWithJs, variables } from '../variables';
import '../box/apt-box';
import '../labeled-icon/apt-labeled-icon';
import '../data-summary/apt-data-summary';

// Note: Your element must have a hyphen in the name (for example, "hello-world"). It's a requirement
// so that our components don't collide with future additions to HTML.
@customElement('apt-incumbent-summary')
export class IncumbentSummary extends LitElement {
  @property({ type: Object })
  compact = { value: false };

  @property({ type: Object })
  filtered = { value: false };

  @property({ type: Number })
  incumbentSellers = 0;

  @property({ type: Number })
  crossed = 0;

  @property({ type: Number })
  sellerCrossed = 0;

  @property({ type: Number })
  totalCrossed = 0;

  static override get styles() {
    return [
      variables,
      svgWithJs,
      css`
        :host {
          display: block;
          box-sizing: border-box;
        }

        .incumbent-summary {
          min-width: 230px;
          min-height: 120px;
          max-width: 240px;

          max-height: 120px;
          font-family: var(--font-family-sans-serif);
          text-align: left;
        }

        .incumbent-summary.compact {
          min-width: 460px;
          min-height: 60px;
          max-width: 460px;
          max-height: 60px;
        }

        .inner-box {
          display: flex;
          flex-direction: column;
          height: 110px;
          min-width: 230px;
          text-align: left;
          padding: 5px;
          max-width: 240px;
        }

        .compact .inner-box {
          flex-direction: row;
          height: 40px;
          min-width: 450px;
        }

        .top-area {
          min-width: 200px;
          max-width: 200px;
        }

        .compact .inner-box .top-area {
          min-width: 200px;
          max-width: 200px;
        }

        .compact .inner-box .bottom-area {
          display: flex;
          align-items: center;
          margin-left: 20px;
        }

        .labeled-icon {
          position: relative;
          left: 0px;
          font-size: 18px;
        }

        .compact .labeled-icon {
          position: initial;
          margin: 2px;
          font-size: 20px;
        }

        .offer-name {
          font-size: 14px;
          font-weight: bold;

          padding-bottom: 8px;
          border-bottom: solid #8dc63f;
          margin-bottom: 10px;
        }

        .compact .offer-name {
          padding-bottom: 0;
          border-bottom: none;
          margin-bottom: 0;
          border-right: solid #8dc63f;

          line-height: 40px;
        }

        .crossed {
          font-size: large;
          font-weight: 500;
        }

        .crossed-percentage {
          font-size: 12px;
          color: #545454;
        }
      `,
    ];
  }

  override render() {
    return html`
      <apt-box class="offer-summary ${this.isCompact()}">
        <div class="inner-box">
          <div class="top-area">
            <div class="offer-name">
              ${this.incumbentSellers} Incumbent Sellers
            </div>
          </div>

          <div class="bottom-area">
            <apt-labeled-icon
              class="labeled-icon"
              color="${this.labeledIconColor(this.crossed, this.totalCrossed)}"
              icon="fa fa-exchange"
              enableTooltip
              position="right"
            >
              <apt-data-summary
                slot="data-summary"
                color="${this.labeledIconColor(
                  this.crossed,
                  this.totalCrossed,
                )}"
                ?filtered="${this.filtered?.value}"
                icon="fa-exchange"
                label="Incumbent Crossed"
                .partial="${this.crossed}"
                .total="${this.totalCrossed}"
              ></apt-data-summary>
            </apt-labeled-icon>
            <div>
              <div class="crossed">
                ${this.crossed}/${this.totalCrossed} Products
              </div>
              <div class="crossed-percentage">
                Plan Crossed ${this.sellerCrossed}%
              </div>
            </div>
          </div>
        </div>
      </apt-box>
    `;
  }

  isCompact() {
    return this.compact?.value === true ? 'compact' : '';
  }

  iconColor(numerator: number, denominator: number) {
    if (denominator > 0) {
      const fraction = numerator / denominator;

      if (fraction < 0.5) {
        return 'danger';
      } else if (fraction < 0.8) {
        return 'warning';
      } else {
        return 'primary';
      }
    } else {
      return 'danger';
    }
  }

  labeledIconColor(numerator: number, denominator: number): string {
    return this.iconColor(numerator, denominator);
  }

  protected override firstUpdated(): void {
    library.add(faEdit);
    library.add(faExchange);
    library.add(faTags);

    // @ts-ignore The dom.watch() TS definition indicates no parameters for this. However, when
    // you actually dig into the code, you see that it looks at the arguments after it gets into
    // the code and tries to pull some out if there aren't any (it even supplies an empty object
    // for options if you don't supply one yourself).
    //
    // Note: We have to wait for the first update for the shadow DOM to even be created.
    dom.watch({
      autoReplaceSvgRoot: this.shadowRoot,
      observeMutationsRoot: this.shadowRoot,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'apt-incumbent-summary': IncumbentSummary;
  }
}
